import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import * as _ from 'lodash';
import {DropDownList, MultiSelect} from '@progress/kendo-react-dropdowns';
import {defaultTemplate} from 'core/hoc';
import {getTrimmedPasteResult} from 'core/utils';

const BaasicDropdownTemplate = function (props) {
    const { store, t, placeholder, disabled, className, warningClassName, customRef, trimOnPaste = true, ...assignProps } = props;

    function onChange(event) {
        onChangeFn(event);
    }

    function onFilter(event) {
        store.onFilter(event.filter.value);
    }

    function handlePasteCapture(e) {
        const result = getTrimmedPasteResult(e);
        if (result) {
            store.onFilter(result.nextValue);
        }
    }

    function onChangeFn(e) {
        if (props.onChange) {
            props.onChange(e);
            if (props.store) {
                props.store.onFilter("");
            }
            return;
        }
        store.onChange(e.target.value);
        if (store) {
            store.onFilter("");
        }
    }

    function getValue() {
        const value = props.value || store.value;
        // const value = _.has(props, 'value') ? props.value : store.value;
        if (_.isString(value) || _.isNumber(value)) {
            return store.items.find(i => i[store.options.dataItemKey] === value);
        }
        else {
            return value;
        }
    }

    // v3-5 used the `label` prop to display placeholder text in the empty
    // dropdown. v14 turned `label` into a floating-label trigger that wraps
    // the widget in .k-floating-label-container (which collapses its width).
    // - DropDownList: communicate empty-state text via `defaultItem`.
    // - MultiSelect: ignores `defaultItem` — it uses the `placeholder` prop.
    const placeholderText = placeholder
        ? t(placeholder)
        : t(store.options.placeholder);

    function getDefaultItem() {
        const item = !_.isNull(store.options.defaultItem) && !_.isUndefined(store.options.defaultItem)
            ? store.options.defaultItem
            : _.find(store.items, i => i.default === true);

        if (item) {
            return {
                ...item,
                [store.options.textField]: t(item[store.options.textField]),
                [store.options.dataItemKey]: item[store.options.dataItemKey] || null
            };
        }
        if (placeholderText) {
            return {
                [store.options.textField]: placeholderText,
                // Stores that key text and value on the same field (e.g. the
                // relationship/gender dropdowns, both `name`) would have this null
                // clobber the placeholder text — only add the empty dataItemKey
                // when it's a distinct field.
                ...(store.options.dataItemKey !== store.options.textField && { [store.options.dataItemKey]: null }),
            };
        }
        return undefined;
    }

    function isMulti() {
        return props.multi != null ? props.multi : store.options.multi;
    }

    const Component = isMulti() ? MultiSelect : DropDownList;

    const hasCustomClass = (className != null && className !== '');
    const hasWarningClass = (warningClassName != null && warningClassName !== '');

    // v14 shows the empty-state text via `defaultItem`, which renders in the
    // same `.k-input-inner` as a real selection — so without a marker the
    // "Select ..." placeholder paints the same near-black as a chosen value.
    // Flag the empty single-select so its placeholder text can be greyed to
    // match the $grey-500 placeholder of text inputs (see plugin.k-dropdown.css).
    const selectedValue = getValue();
    const showsPlaceholder = !isMulti()
        && (selectedValue == null || selectedValue[store.options.dataItemKey] == null);

    const styleClasses = classNames({
        [className]: hasCustomClass,
        [warningClassName]: hasWarningClass,
        'dropdown--placeholder': showsPlaceholder,
    });

    const dropdown = (
        <Component
            {...assignProps}
            className={styleClasses}
            data={_.filter(store.items, i => i.hideInDropdown !== true)}
            textField={store.options.textField}
            filter={store.filterTerm}
            autoClose={props.autoClose}
            dataItemKey={store.options.dataItemKey}
            filterable={store.options.filterable}
            disabled={store.options.disabled || disabled}
            onFilterChange={onFilter}
            onChange={onChange}
            loading={store.loading}
            onOpen={store.onOpen}
            onClose={store.onClose}
            value={selectedValue}
            defaultItem={isMulti() ? undefined : getDefaultItem()}
            placeholder={isMulti() ? placeholderText : undefined}
            popupSettings={store.options.popupSettings}
            ref={customRef}
            focused={props.focused}
        />
    );

    return trimOnPaste
        ? <div className="dropdown__paste-wrap" onPasteCapture={handlePasteCapture}>{dropdown}</div>
        : dropdown;
};

BaasicDropdownTemplate.propTypes = {
    store: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    className: PropTypes.string,
    warningClassName: PropTypes.string,
    placeholder: PropTypes.string,
    disabled: PropTypes.bool,
    value: PropTypes.any,
    multi: PropTypes.bool,
    t: PropTypes.func,
    focused: PropTypes.bool,
    autoClose: PropTypes.bool,
    customRef: PropTypes.object,
    trimOnPaste: PropTypes.bool
};

export default defaultTemplate(BaasicDropdownTemplate);
